import {
  CONTENT_FORMAT_GROUPS,
  CONTENT_FORMAT_GROUP_LABELS,
  CONTENT_FORMAT_LABELS,
  isEventFormat,
  PILLARS,
  PILLAR_DESCRIPTIONS,
  PILLAR_LABELS,
  type AreaDto,
  type ContentFormat,
  type ContentFormatGroup,
  type ContentItemDto,
  type Pillar,
} from '@nova/shared';
import { useState } from 'react';
import { Button } from '../../components/Button.js';
import { Disclosure } from '../../components/Disclosure.js';
import { Drawer } from '../../components/Drawer.js';
import { cn } from '../../lib/cn.js';
import { useChannels, useCreateContent } from './useContent.js';

/** Human-entered content always starts as a suggestion, like the agents' own. */
export function NewContentDialog({
  open,
  onClose,
  areas,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  areas: AreaDto[];
  onCreated: (item: ContentItemDto) => void;
}) {
  const create = useCreateContent();
  const { data: channels = [] } = useChannels();

  const [title, setTitle] = useState('');
  const [areaId, setAreaId] = useState('');
  const [pillar, setPillar] = useState<Pillar>('FAG');
  const [format, setFormat] = useState<ContentFormat>('ARTIKKEL');
  const [summary, setSummary] = useState('');
  const [draftDeadline, setDraftDeadline] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [channelIds, setChannelIds] = useState<string[]>([]);

  const valid = title.trim().length >= 3 && areaId;
  const showEventDate = isEventFormat(format);

  function reset() {
    setTitle('');
    setAreaId('');
    setPillar('FAG');
    setFormat('ARTIKKEL');
    setSummary('');
    setDraftDeadline('');
    setEventDate('');
    setChannelIds([]);
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Nytt innhold"
      subtitle={
        <p className="text-ink-soft text-sm">Legges inn som forslag, klart til vurdering.</p>
      }
      footer={
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            disabled={!valid}
            loading={create.isPending}
            onClick={() =>
              create.mutate(
                {
                  title: title.trim(),
                  areaId,
                  pillar,
                  format,
                  summary: summary.trim() || null,
                  draftDeadline: draftDeadline ? new Date(draftDeadline).toISOString() : null,
                  eventDate:
                    showEventDate && eventDate ? new Date(eventDate).toISOString() : null,
                  channelIds,
                },
                {
                  onSuccess: (item) => {
                    reset();
                    onCreated(item as ContentItemDto);
                  },
                },
              )
            }
          >
            Opprett forslag
          </Button>
          <Button size="sm" onClick={onClose}>
            Avbryt
          </Button>
          {create.isError && (
            <span className="text-danger text-xs">{(create.error as Error).message}</span>
          )}
        </div>
      }
    >
      {/* Minimal by default: a card is really just a title and an area. Pillar is
          a quick single choice. Everything else — format, dates, brief, channels —
          waits behind «Flere valg» so creating feels like two or three fields, not
          a form. Nothing is lost; Nova fills sensible defaults and the rest is
          editable on the card afterwards. */}
      <div className="space-y-5">
        <div>
          <label htmlFor="new-title" className="label-micro mb-1.5 block">
            Tittel
          </label>
          <input
            id="new-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
            placeholder="F.eks. «Kundecase: Slik sparte kommunen 400 timer»"
            className="border-hairline bg-base/40 text-ink placeholder:text-ink-soft/50 h-10 w-full rounded-lg border px-3 text-sm"
          />
        </div>

        <div>
          <label htmlFor="new-area" className="label-micro mb-1.5 block">
            Område
          </label>
          <select
            id="new-area"
            value={areaId}
            onChange={(e) => setAreaId(e.target.value)}
            className="border-hairline bg-base/40 text-ink h-10 w-full rounded-lg border px-3 text-sm"
          >
            <option value="">Velg område …</option>
            {areas.map((area) => (
              <option key={area.id} value={area.id}>
                {area.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <p className="label-micro mb-1.5">Innholdspilar</p>
          {/* Compact labels up front; the longer explanations live under «Flere
              valg» so this stays a one-glance choice. */}
          <div className="flex flex-wrap gap-1.5">
            {PILLARS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setPillar(option)}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-sm transition',
                  pillar === option
                    ? 'border-accent/40 bg-accent/10 text-accent'
                    : 'border-hairline bg-base/30 text-ink-soft hover:border-accent/25 hover:text-ink',
                )}
              >
                {PILLAR_LABELS[option]}
              </button>
            ))}
          </div>
          <p className="text-ink-soft/70 mt-1.5 text-[11px] leading-snug">
            {PILLAR_DESCRIPTIONS[pillar]}
          </p>
        </div>

        <Disclosure
          label="Flere valg"
          hint="format, frist, brief, kanaler"
          className="border-hairline border-t pt-3"
        >
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="new-format" className="label-micro mb-1.5 block">
                  Format
                </label>
                <select
                  id="new-format"
                  value={format}
                  onChange={(e) => setFormat(e.target.value as ContentFormat)}
                  className="border-hairline bg-base/40 text-ink h-10 w-full rounded-lg border px-3 text-sm"
                >
                  {(Object.keys(CONTENT_FORMAT_GROUPS) as ContentFormatGroup[]).map((group) => (
                    <optgroup key={group} label={CONTENT_FORMAT_GROUP_LABELS[group]}>
                      {CONTENT_FORMAT_GROUPS[group].map((option) => (
                        <option key={option} value={option}>
                          {CONTENT_FORMAT_LABELS[option]}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="new-deadline" className="label-micro mb-1.5 block">
                  Frist for utkast
                </label>
                <input
                  id="new-deadline"
                  type="date"
                  value={draftDeadline}
                  onChange={(e) => setDraftDeadline(e.target.value)}
                  className="border-hairline bg-base/40 text-ink h-10 w-full rounded-lg border px-3 text-sm"
                />
              </div>
            </div>

            {/* Events (webinar/arrangement) carry a date of execution, distinct
                from when we publish about them. */}
            {showEventDate && (
              <div>
                <label htmlFor="new-event-date" className="label-micro mb-1.5 block">
                  Dato for gjennomføring
                </label>
                <input
                  id="new-event-date"
                  type="datetime-local"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  className="border-hairline bg-base/40 text-ink h-10 w-full rounded-lg border px-3 text-sm"
                />
                <p className="text-ink-soft mt-1 text-[11px]">
                  Når {CONTENT_FORMAT_LABELS[format].toLowerCase()}et faktisk holdes — ikke når vi
                  publiserer om det.
                </p>
              </div>
            )}

            <div>
              <label htmlFor="new-summary" className="label-micro mb-1.5 block">
                Brief
              </label>
              <textarea
                id="new-summary"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={4}
                placeholder="Hva skal saken handle om, og for hvem? Nova bruker dette når den skriver utkastet."
                className="border-hairline bg-base/40 text-ink placeholder:text-ink-soft/50 w-full resize-y rounded-lg border p-3 text-sm"
              />
            </div>

            <div>
              <p className="label-micro mb-1.5">Aktuelle kanaler</p>
              <div className="flex flex-wrap gap-1.5">
                {channels.map((channel) => {
                  const active = channelIds.includes(channel.id);
                  return (
                    <button
                      key={channel.id}
                      type="button"
                      onClick={() =>
                        setChannelIds((current) =>
                          active
                            ? current.filter((id) => id !== channel.id)
                            : [...current, channel.id],
                        )
                      }
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-[11px] transition',
                        active
                          ? 'border-accent/40 bg-accent/15 text-accent'
                          : 'border-hairline bg-raised/50 text-ink-soft hover:text-ink',
                      )}
                    >
                      {channel.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </Disclosure>
      </div>
    </Drawer>
  );
}
